import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import { ConsolidatedRow, CrosswalkSummaryTableProps } from '../utils/DataTypes';
import { CROSSWALK_SUMMARY_COLUMN_MAPPING } from '../constants';

const ClientCrosswalkSummaryTable: React.FC<CrosswalkSummaryTableProps> = ({ clientNumber, category }) => {
  const dataprovider = useDataProvider();
  const notify = useNotify();

  const [crosswalkData, setCrosswalkData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const allRecords = crosswalkData?.[category] || [];
  const totalCount = allRecords.length;

  const currentRecords = allRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const categoryTitle = category.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const columns = CROSSWALK_SUMMARY_COLUMN_MAPPING[category];

  useEffect(() => {
    if (!clientNumber) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    const fetchCrossWalks = async () => {
      try {
        const response = await dataprovider.getOne('ClientCrosswalks', { id: clientNumber });

        setCrosswalkData(response.data.records);
      } catch (error) {
        notify(`Failed to load crosswalk data for ${clientNumber},`, { type: 'error' });
        setCrosswalkData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCrossWalks();
  }, [clientNumber, dataprovider, notify]);
  return (
    <Box sx={{ mt: 2, mb: 1, border: '1px solid #eee', borderRadius: 0, overflow: 'hidden' }}>
      <Typography variant="subtitle2" sx={{ p: 1, bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
        {categoryTitle} ({totalCount} unique records)
      </Typography>
      {isLoading ? (
        <Typography color="text.secondary" sx={{ py: 2, px: 1 }}>
          Loading {category} summary...
        </Typography>
      ) : totalCount === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No active crosswalk entries found for this category.
        </Typography>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 200, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        fontWeight: 'bold',
                        width: col.width,
                        color: 'primary.main',
                        py: 1,
                        fontSize: '0.85rem',
                        bgcolor: '#fff',
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRecords.map((row: ConsolidatedRow, index: number) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {columns.map((col) => (
                      <TableCell key={col.key} component="th" scope="row" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                        {row[col.key] || '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > rowsPerPage && (
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              sx={{
                border: 'none',
                '& .MuiTablePagination-selectLabel': { display: 'none' },
                '& .MuiTablePagination-input': { display: 'none' },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ClientCrosswalkSummaryTable;
